import { TeButtonOutline, TeCallout, TeContainerSlim } from '@topcon/tuicl-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGeolocation } from '../../../hooks/useGeolocation';
import { GeolocationStatusEnum } from '../../../types/geolocation.types';

function GeolocationCallout() {
  const { t } = useTranslation();
  const { country, status, startCountryDetection, retryReverseGeocode } = useGeolocation();

  const calloutStatus = useMemo(() => {
    switch (status) {
      case GeolocationStatusEnum.PermissionDenied:
      case GeolocationStatusEnum.Error:
        return 'warning';
      case GeolocationStatusEnum.Success:
      case GeolocationStatusEnum.Idle:
      case GeolocationStatusEnum.ResolvingCountry:
      case GeolocationStatusEnum.ResolvingLocation:
      default:
        return 'info';
    }
  }, [status]);

  const calloutIcon = useMemo(() => {
    switch (status) {
      case GeolocationStatusEnum.Error:
      case GeolocationStatusEnum.PermissionDenied:
        return 'warning';
      case GeolocationStatusEnum.Success:
      case GeolocationStatusEnum.ResolvingLocation:
      case GeolocationStatusEnum.ResolvingCountry:
      default:
        return 'info';
    }
  }, [status]);

  const calloutHeading = useMemo(() => {
    switch (status) {
      case GeolocationStatusEnum.Idle:
        return t('geolocationCallout.idle.heading');
      case GeolocationStatusEnum.PermissionDenied:
        return t('geolocationCallout.permission-denied.heading');
      case GeolocationStatusEnum.ResolvingLocation:
        return t('geolocationCallout.resolving-location.heading');
      case GeolocationStatusEnum.ResolvingCountry:
        return t('geolocationCallout.resolving-country.heading');
      case GeolocationStatusEnum.Success:
        return t('geolocationCallout.success.heading');
      case GeolocationStatusEnum.Error:
        return t('geolocationCallout.error.heading');
      default:
        return '';
    }
  }, [status, t]);

  const calloutText = useMemo(() => {
    switch (status) {
      case GeolocationStatusEnum.Idle:
        return t('geolocationCallout.idle.text');
      case GeolocationStatusEnum.PermissionDenied:
        return t('geolocationCallout.permission-denied.text');
      case GeolocationStatusEnum.ResolvingLocation:
        return t('geolocationCallout.resolving-location.text');
      case GeolocationStatusEnum.ResolvingCountry:
        return t('geolocationCallout.resolving-country.text');
      case GeolocationStatusEnum.Success:
        return t('geolocationCallout.success.text', { country: country?.name });
      case GeolocationStatusEnum.Error:
        return t('geolocationCallout.error.text');
      default:
        return '';
    }
  }, [status, country, t]);

  if (status === GeolocationStatusEnum.Unsupported) {
    return null;
  }

  return (
    <TeCallout
      grow
      status={calloutStatus}
      icon={calloutIcon}
      heading={calloutHeading}
      text={calloutText}
    >
      {status === GeolocationStatusEnum.Idle && (
        <TeContainerSlim grow padding={['s', 0, 0, 0]}>
          <TeButtonOutline
            size="s"
            grow
            icon="my_location"
            teclick={startCountryDetection}
            text={t('geolocationCallout.buttonLabel')}
          />
        </TeContainerSlim>
      )}
      {status === GeolocationStatusEnum.Error && (
        <TeContainerSlim grow padding={['s', 0, 0, 0]}>
          <TeButtonOutline
            size="s"
            grow
            icon="refresh"
            borderColor="border.warning.onBackground"
            textColor="text.warning.onBackground"
            iconColor="text.warning.onBackground"
            teclick={retryReverseGeocode}
            text={t('geolocationCallout.retryButtonLabel')}
          />
        </TeContainerSlim>
      )}
    </TeCallout>
  );
}

export default GeolocationCallout;
